export type BillingCadence = 'monthly' | 'quarterly' | 'yearly' | 'custom';
export type RuleMetric = 'video_minutes' | 'storage' | 'service_combo';
export type RuleOperator = '<' | '<=' | '=' | '!=' | '>=' | '>';
export type RuleRateType = 'per_minute' | 'per_gb' | 'flat' | 'slab_flat' | 'slab_progressive';
export type ServiceComboMatchMode = 'exact' | 'contains';
export type ServiceComboRateMode = 'override' | 'additive';
export type CarryoverCapType = 'fixed' | 'percent_of_included';
export type CarryoverConsumptionOrder = 'carryover_first' | 'current_cycle_first';

export interface BillingOrgSettings {
  id: string;
  organisation_id: string;
  currency: string;
  default_tax_rate_percent: number;
  prices_tax_inclusive: boolean;
  tax_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingOrgSettingsUpsertInput {
  currency?: string;
  default_tax_rate_percent?: number;
  prices_tax_inclusive?: boolean;
  tax_label?: string | null;
}

export interface BillingPlan {
  id: string;
  organisation_id: string | null;
  name: string;
  description: string | null;
  cadence: BillingCadence;
  billing_timezone: string;
  cycle_anchor_day: number;
  currency: string;
  base_subscription_fee: number;
  is_active: boolean;
  valid_from: string;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingPlanCreateInput {
  organisation_id?: string | null;
  name: string;
  description?: string | null;
  cadence?: BillingCadence;
  billing_timezone?: string;
  cycle_anchor_day?: number;
  currency?: string;
  base_subscription_fee?: number;
  is_active?: boolean;
  valid_from?: string;
  valid_to?: string | null;
}

export interface BillingPlanUpdateInput {
  organisation_id?: string | null;
  name?: string;
  description?: string | null;
  cadence?: BillingCadence;
  billing_timezone?: string;
  cycle_anchor_day?: number;
  currency?: string;
  base_subscription_fee?: number;
  is_active?: boolean;
  valid_from?: string;
  valid_to?: string | null;
}

export interface BillingPlanEntitlement {
  id: string;
  billing_plan_id: string;
  metric: 'video_minutes' | 'storage';
  included_units: number;
  carryover_enabled: boolean;
  carryover_cap_type: CarryoverCapType | null;
  carryover_cap_value: number | null;
  carryover_expiry_cycles: number;
  carryover_consumption_order: CarryoverConsumptionOrder;
  created_at: string;
  updated_at: string;
}

export interface BillingPlanEntitlementCreateInput {
  billing_plan_id: string;
  metric: 'video_minutes' | 'storage';
  included_units: number;
  carryover_enabled?: boolean;
  carryover_cap_type?: CarryoverCapType | null;
  carryover_cap_value?: number | null;
  carryover_expiry_cycles?: number;
  carryover_consumption_order?: CarryoverConsumptionOrder;
}

export interface BillingPlanEntitlementUpdateInput {
  included_units?: number;
  carryover_enabled?: boolean;
  carryover_cap_type?: CarryoverCapType | null;
  carryover_cap_value?: number | null;
  carryover_expiry_cycles?: number;
  carryover_consumption_order?: CarryoverConsumptionOrder;
}

export interface BillingPricingRule {
  id: string;
  organisation_id: string | null;
  metric: RuleMetric;
  name: string;
  description: string | null;
  priority: number;
  rate_type: RuleRateType;
  rate_value: number;
  service_combo_match_mode: ServiceComboMatchMode | null;
  service_combo_rate_mode: ServiceComboRateMode | null;
  service_combo_services: string[] | null;
  is_active: boolean;
  valid_from: string;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingPricingRuleCreateInput {
  organisation_id?: string | null;
  metric: RuleMetric;
  name: string;
  description?: string | null;
  priority?: number;
  rate_type: RuleRateType;
  rate_value: number;
  service_combo_match_mode?: ServiceComboMatchMode | null;
  service_combo_rate_mode?: ServiceComboRateMode | null;
  service_combo_services?: string[] | null;
  is_active?: boolean;
  valid_from?: string;
  valid_to?: string | null;
}

/**
 * Field keys for pricing rule conditions. Values must exist on billingCalculationService.baseValues.
 * @see billingCalculationService.baseValues and billingRuleEvaluator.evaluateRuleConditions
 *
 * For video_minutes rules (e.g. 8v8 vs 11v11), use max_players_in_starting_lineup so that
 * the rule matches when the org's maximum lineup size fits (e.g. 11v11: max <= 11).
 * Using players_in_starting_lineup (average) can fail when average is above the threshold
 * (e.g. 11.5 fails both <= 8 and <= 11), resulting in zero video amount.
 */
export const BILLING_CONDITION_FIELD_KEYS = [
  { value: 'players_in_starting_lineup', label: 'Average player count (starting lineup)' },
  { value: 'max_players_in_starting_lineup', label: 'Max player count (starting lineup)' },
  { value: 'video_minutes', label: 'Video minutes (usage)' },
  { value: 'storage_gb', label: 'Storage GB (usage)' },
] as const;

export type BillingConditionFieldKey = (typeof BILLING_CONDITION_FIELD_KEYS)[number]['value'];

export interface BillingPricingRuleCondition {
  id: string;
  billing_pricing_rule_id: string;
  field_key: string;
  operator: RuleOperator;
  field_value: string;
  created_at: string;
}

export interface BillingPricingRuleConditionCreateInput {
  billing_pricing_rule_id: string;
  field_key: string;
  operator: RuleOperator;
  field_value: string;
}

export interface BillingInvoice {
  id: string;
  organisation_id: string;
  billing_plan_id: string | null;
  invoice_number: string;
  invoice_type: 'subscription_start' | 'usage_settlement_end' | 'adjustment_only';
  period_start: string;
  period_end: string;
  currency: string;
  subtotal_amount: number;
  tax_amount: number;
  tax_rate_percent: number;
  prices_tax_inclusive: boolean;
  tax_label: string | null;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  status: 'draft' | 'issued' | 'partially_paid' | 'paid' | 'overdue' | 'void';
  due_at: string | null;
  issued_at: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingInvoiceLineItem {
  id: string;
  invoice_id: string;
  line_type: 'subscription' | 'usage' | 'adjustment' | 'tax';
  metric: string | null;
  description: string;
  quantity: number | null;
  unit_price: number | null;
  amount: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface BillingInvoiceLineItemCreateInput {
  line_type: 'subscription' | 'usage' | 'adjustment' | 'tax';
  metric?: string | null;
  description: string;
  quantity?: number | null;
  unit_price?: number | null;
  amount: number;
  metadata?: Record<string, unknown> | null;
}

export interface BillingInvoiceCreateInput {
  organisation_id: string;
  billing_plan_id?: string | null;
  invoice_type: BillingInvoice['invoice_type'];
  period_start: string;
  period_end: string;
  currency: string;
  subtotal_amount: number;
  tax_amount?: number;
  /** When tax-inclusive, pass the total (customer-facing amount) so amount_due is correct */
  total_amount?: number;
  tax_rate_percent?: number;
  prices_tax_inclusive?: boolean;
  tax_label?: string | null;
  due_at?: string | null;
  status?: BillingInvoice['status'];
  line_items: BillingInvoiceLineItemCreateInput[];
}
